<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { useDataSourcesStore } from '@/stores/dataSources'

const LOG_TYPE_COLORS = {
  conn: '#7357D2',
  dns: '#4A90D9',
  http: '#50C878',
  ssl: '#F5A623',
  x509: '#D94A4A',
  files: '#9B59B6',
  smtp: '#1ABC9C',
  ssh: '#E74C3C',
  dhcp: '#3498DB',
  ntp: '#95A5A6',
  rdp: '#E67E22',
  ftp: '#27AE60',
  sip: '#8E44AD',
  snmp: '#2980B9',
  kerberos: '#C0392B',
  smb: '#16A085',
  dce_rpc: '#D35400',
  ntlm: '#7F8C8D',
  pe: '#BDC3C7',
  weird: '#E91E63',
  notice: '#FF5722',
  software: '#607D8B',
  known: '#795548',
  _default: '#6B7280'
}

function getLogTypeColor(logType) {
  return LOG_TYPE_COLORS[logType] || LOG_TYPE_COLORS._default
}

defineProps({
  disabled: {
    type: Boolean,
    default: false
  }
})

const store = useDataSourcesStore()

const isOpen = ref(false)
const triggerRef = ref(null)
const menuRef = ref(null)
const menuStyle = ref({})
const expandedSources = ref({})

const activeSources = computed(() => store.activeSourceGroups)
const isFiltered = computed(() => !store.isAllSelected)

function getSourceCheckState(sourceId) {
  if (store.noneSelected) return 'none'
  if (store.selectedLogTypes.length === 0) return 'full'
  if (store.isSourceFullySelected(sourceId)) return 'full'
  if (store.isSourcePartiallySelected(sourceId)) return 'partial'
  return 'none'
}

function isLogTypeSelected(logType) {
  if (store.noneSelected) return false
  return store.selectedLogTypes.length === 0 || store.selectedLogTypes.includes(logType)
}

function getSourceLogTypes(sourceId) {
  return store.sourceLogTypeMap[sourceId] || []
}

function handleSourceToggle(sourceId) {
  store.toggleSource(sourceId)
}

function handleLogTypeToggle(logType) {
  store.toggleLogType(logType)
}

function handleReset() {
  store.resetSelection()
  close()
}

function toggleSourceExpand(sourceId, event) {
  event.stopPropagation()
  expandedSources.value = {
    ...expandedSources.value,
    [sourceId]: !expandedSources.value[sourceId]
  }
  nextTick(updateMenuPosition)
}

// Dropdown positioning (same pattern as NotificationSettingsDropdown)
function updateMenuPosition() {
  if (!triggerRef.value || !isOpen.value) return

  const rect = triggerRef.value.getBoundingClientRect()
  const menuWidth = 260
  const menuHeight = menuRef.value?.offsetHeight || 300

  let left = rect.left
  let top = rect.bottom + 6

  if (top + menuHeight > window.innerHeight - 10) {
    top = rect.top - menuHeight - 6
  }
  if (left + menuWidth > window.innerWidth - 10) {
    left = window.innerWidth - menuWidth - 10
  }
  if (left < 10) left = 10

  menuStyle.value = {
    position: 'fixed',
    top: `${top}px`,
    left: `${left}px`,
    width: `${menuWidth}px`
  }
}

function toggle(event) {
  event.stopPropagation()
  isOpen.value = !isOpen.value
}

function close() {
  isOpen.value = false
}

watch(isOpen, async (val) => {
  if (val) {
    // Auto-expand sources that have selections
    if (store.selectedLogTypes.length > 0) {
      for (const source of activeSources.value) {
        if (store.isSourcePartiallySelected(source.id) || store.isSourceFullySelected(source.id)) {
          expandedSources.value[source.id] = true
        }
      }
    }
    await nextTick()
    updateMenuPosition()
  }
})

function handleClickOutside(event) {
  if (triggerRef.value && triggerRef.value.contains(event.target)) return
  if (menuRef.value && menuRef.value.contains(event.target)) return
  close()
}

function handleKeydown(event) {
  if (event.key === 'Escape') close()
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside, true)
  document.addEventListener('keydown', handleKeydown)
  window.addEventListener('resize', updateMenuPosition)
  window.addEventListener('scroll', updateMenuPosition, true)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside, true)
  document.removeEventListener('keydown', handleKeydown)
  window.removeEventListener('resize', updateMenuPosition)
  window.removeEventListener('scroll', updateMenuPosition, true)
})
</script>

<template>
  <div class="source-log-selector">
    <button
      type="button"
      ref="triggerRef"
      class="selector-btn"
      :class="{
        'is-open': isOpen,
        'is-filtered': isFiltered
      }"
      :disabled="disabled"
      @click="toggle"
    >
      <font-awesome-icon :icon="['fas', 'database']" class="selector-icon" />
      <span class="selector-label">{{ store.selectorLabel }}</span>
      <button
        v-if="isFiltered"
        type="button"
        class="reset-x"
        @click.stop="handleReset"
        title="Reset to all sources"
      >
        <font-awesome-icon :icon="['fas', 'xmark']" />
      </button>
      <font-awesome-icon v-else :icon="['fas', 'chevron-down']" class="selector-chevron" />
    </button>

    <Teleport to="body">
      <Transition name="selector-dropdown">
        <div
          v-if="isOpen"
          ref="menuRef"
          class="selector-menu"
          :style="menuStyle"
          @click.stop
        >
          <!-- Source rows -->
          <div
            v-for="source in activeSources"
            :key="source.id"
            class="source-group"
          >
            <div
              class="menu-option source-row"
              :class="{ 'is-expanded': expandedSources[source.id] }"
              @click="handleSourceToggle(source.id)"
            >
              <span class="option-check-space">
                <span
                  class="checkbox"
                  :class="{
                    'is-checked': getSourceCheckState(source.id) === 'full',
                    'is-indeterminate': getSourceCheckState(source.id) === 'partial'
                  }"
                >
                  <font-awesome-icon
                    v-if="getSourceCheckState(source.id) === 'full'"
                    :icon="['fas', 'check']"
                    class="check-icon"
                  />
                  <font-awesome-icon
                    v-else-if="getSourceCheckState(source.id) === 'partial'"
                    :icon="['fas', 'minus']"
                    class="check-icon"
                  />
                </span>
              </span>
              <span class="option-label source-label">{{ source.label }}</span>
              <button
                type="button"
                class="expand-chevron"
                @click="toggleSourceExpand(source.id, $event)"
              >
                <font-awesome-icon
                  :icon="['fas', expandedSources[source.id] ? 'chevron-up' : 'chevron-down']"
                />
              </button>
            </div>

            <!-- Log types (expandable) -->
            <Transition name="logs-expand">
              <div v-if="expandedSources[source.id]" class="log-types-list">
                <div
                  v-for="logType in getSourceLogTypes(source.id)"
                  :key="logType"
                  class="menu-option log-type-row"
                  :class="{ 'is-selected': isLogTypeSelected(logType) }"
                  @click="handleLogTypeToggle(logType)"
                >
                  <span class="option-check-space">
                    <span
                      class="checkbox small"
                      :class="{ 'is-checked': isLogTypeSelected(logType) }"
                    >
                      <font-awesome-icon
                        v-if="isLogTypeSelected(logType)"
                        :icon="['fas', 'check']"
                        class="check-icon"
                      />
                    </span>
                  </span>
                  <span
                    class="log-type-dot"
                    :style="{ background: getLogTypeColor(logType) }"
                  ></span>
                  <span class="option-label">{{ logType }}</span>
                </div>
              </div>
            </Transition>
          </div>

          <!-- Reset to All (bottom, always visible when filtered) -->
          <template v-if="isFiltered">
            <div class="menu-divider"></div>
            <div
              class="menu-option reset-option"
              @click="handleReset"
            >
              <span class="option-check-space">
                <font-awesome-icon :icon="['fas', 'rotate-left']" class="reset-icon" />
              </span>
              <span class="option-label">Reset to All</span>
            </div>
          </template>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped>
.source-log-selector {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.selector-btn {
  height: 32px;
  padding: 0 0.625rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.375rem;
  color: #9CA3AF;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}

.selector-btn:hover:not(:disabled) {
  color: #E5E7EB;
  border-color: rgba(115, 87, 210, 0.4);
  background: rgba(115, 87, 210, 0.1);
}

.selector-btn.is-open {
  background: rgba(115, 87, 210, 0.15);
  border-color: rgba(115, 87, 210, 0.4);
  color: #E5E7EB;
}

.selector-btn.is-filtered {
  background: rgba(115, 87, 210, 0.12);
  border-color: rgba(115, 87, 210, 0.3);
  color: #E5E7EB;
}

.selector-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.selector-icon {
  font-size: 0.7rem;
  opacity: 0.6;
}

.selector-btn.is-filtered .selector-icon {
  color: #a78bfa;
  opacity: 1;
}

.selector-label {
  font-weight: 500;
}

.selector-chevron {
  font-size: 0.5rem;
  opacity: 0.5;
  transition: transform 0.2s;
}

.selector-btn.is-open .selector-chevron {
  transform: rotate(180deg);
}

.reset-x {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  background: rgba(255, 255, 255, 0.08);
  border: none;
  border-radius: 50%;
  color: #9CA3AF;
  font-size: 0.5rem;
  cursor: pointer;
  transition: all 0.15s ease;
}

.reset-x:hover {
  background: rgba(255, 255, 255, 0.15);
  color: #E5E7EB;
}

/* Dropdown menu */
.selector-menu {
  background: #1a1f28;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.625rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.35);
  z-index: 9999;
  overflow: hidden;
  max-height: 400px;
  overflow-y: auto;
}

.menu-option {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  transition: background 0.15s ease;
}

.menu-option:hover {
  background: rgba(255, 255, 255, 0.04);
}

.menu-divider {
  height: 1px;
  background: rgba(255, 255, 255, 0.06);
  margin: 0.25rem 0;
}

.reset-option {
  color: #9CA3AF;
}

.reset-option:hover {
  color: #E5E7EB;
}

.reset-icon {
  font-size: 0.7rem;
  color: #9CA3AF;
}

.option-check-space {
  width: 1rem;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.option-label {
  font-size: 0.8125rem;
  color: #E5E7EB;
}

/* Checkbox */
.checkbox {
  width: 1rem;
  height: 1rem;
  border: 1.5px solid rgba(255, 255, 255, 0.2);
  border-radius: 0.1875rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
}

.checkbox.small {
  width: 0.875rem;
  height: 0.875rem;
}

.checkbox.is-checked {
  background: rgba(115, 87, 210, 0.6);
  border-color: #7357D2;
}

.checkbox.is-indeterminate {
  background: rgba(115, 87, 210, 0.3);
  border-color: rgba(115, 87, 210, 0.6);
}

.check-icon {
  font-size: 0.5rem;
  color: #fff;
}

.checkbox.small .check-icon {
  font-size: 0.45rem;
}

/* Source row */
.source-row {
  padding-right: 0.5rem;
}

.source-label {
  flex: 1;
  font-weight: 500;
}

.expand-chevron {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 0.25rem;
  color: #6B7280;
  font-size: 0.5rem;
  cursor: pointer;
  transition: all 0.15s ease;
}

.expand-chevron:hover {
  background: rgba(255, 255, 255, 0.06);
  color: #E5E7EB;
}

/* Log type rows */
.log-types-list {
  overflow: hidden;
}

.log-type-row {
  padding-left: 1.5rem;
  padding-top: 0.375rem;
  padding-bottom: 0.375rem;
}

.log-type-row.is-selected {
  background: rgba(115, 87, 210, 0.06);
}

.log-type-dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  flex-shrink: 0;
}

/* Animations */
.selector-dropdown-enter-active,
.selector-dropdown-leave-active {
  transition: all 0.15s ease;
}

.selector-dropdown-enter-from,
.selector-dropdown-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.logs-expand-enter-active,
.logs-expand-leave-active {
  transition: all 0.15s ease;
  overflow: hidden;
}

.logs-expand-enter-from,
.logs-expand-leave-to {
  opacity: 0;
  max-height: 0;
}

.logs-expand-enter-to,
.logs-expand-leave-from {
  max-height: 500px;
}

/* Scrollbar */
.selector-menu::-webkit-scrollbar {
  width: 4px;
}

.selector-menu::-webkit-scrollbar-track {
  background: transparent;
}

.selector-menu::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
}
</style>
